/**
 * BreakawayNewsModal — every same-day article for a Breakaway pick
 * (Massive + Finnhub, deduped — Yahoo is not used here). Rows mirror the Watchlist news
 * feed (thumbnail, title, keyword chips, summary, provider/sentiment chip).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Newspaper, Zap } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  ProviderSentimentChip,
  NewsSentimentSummary,
  MassiveInsightReasoning,
  KeywordChipRow,
  NewsSourceMix,
} from '@/components/news/NewsBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '');

export default function BreakawayNewsModal({ pick, date, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const ticker = pick?.ticker;

  useEffect(() => {
    if (!open || !ticker) return undefined;
    let cancelled = false;
    let timer = null;
    setLoading(true); setData(null);
    const params = date ? { date } : {};
    // First answer is instant (cache or the scanner's own headline). While
    // the server reports `partial`/`stale`, re-poll a couple of times so the
    // full provider list fills in without ever blocking the modal.
    const load = (attempt) => {
      axios.get(`${API}/breakaway/news/${ticker}`, { params, timeout: 20000 })
        .then((r) => {
          if (cancelled) return;
          setData((prev) => {
            const next = r.data || { articles: [] };
            if (prev && (next.articles || []).length < (prev.articles || []).length) return prev;
            return next;
          });
          if ((r.data?.partial || r.data?.stale) && attempt < 3) {
            timer = setTimeout(() => load(attempt + 1), attempt === 0 ? 4000 : 8000);
          }
        })
        .catch(() => { if (!cancelled && attempt === 0) setData({ articles: [] }); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    load(0);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [open, ticker, date]);

  const all = data?.articles || [];
  const articles = all.filter((a) => a.relevant !== false);
  const mentions = all.filter((a) => a.relevant === false);
  const sameDay = articles.filter((a) => a.same_day);
  const afterClose = articles.filter((a) => !a.same_day);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0" data-testid="breakaway-news-modal">
        <DialogHeader className="px-4 pt-4 pb-2 pr-12 text-left">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Newspaper className="w-4 h-4 text-sky-500" />
            <span data-testid="breakaway-news-ticker">{ticker}</span>
            <span className="text-sm font-normal text-muted-foreground">Breaking news · {data?.date || date || 'today'}</span>
            {pick?.gap_pct != null && (
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">+{pick.gap_pct}% gap · {pick.minute_rvol}× vol</span>
            )}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            {pick?.name}
            {!loading && articles.length >= 2 && <NewsSentimentSummary articles={articles} />}
            {!loading && articles.length > 0 && <NewsSourceMix articles={articles} />}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="px-4 py-6 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {articles.length === 0 && (
              <div className="px-4 py-4 text-xs text-muted-foreground" data-testid="breakaway-news-empty">
                No articles about {data?.company_name || ticker} dated {data?.date || 'today'} on Massive or Finnhub.
              </div>
            )}
            <Section label={`About ${ticker} · dated ${data?.date} · ${sameDay.length}`} rows={sameDay} ticker={ticker} offset={0} />
            {afterClose.length > 0 && (
              <Section label={`About ${ticker} · after the prior close · ${afterClose.length}`} rows={afterClose} ticker={ticker} offset={sameDay.length} />
            )}
            {mentions.length > 0 && (
              <Section
                label={`Mentioned in · ${mentions.length} roundup${mentions.length === 1 ? '' : 's'} — not about ${ticker}, does not count as breaking news`}
                rows={mentions} ticker={ticker} offset={articles.length} muted
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ label, rows, ticker, offset, muted = false }) {
  if (rows.length === 0) return null;
  return (
    <div className={muted ? 'opacity-70' : ''} data-testid={muted ? 'breakaway-news-mentions' : undefined}>
      <div className="px-4 py-1.5 bg-muted/30 border-y border-border/30 text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
      <div className="divide-y divide-border/20">
        {rows.map((article, i) => <ArticleRow key={`${article.url}-${i}`} article={article} ticker={ticker} idx={offset + i} />)}
      </div>
    </div>
  );
}

function ArticleRow({ article, ticker, idx }) {
  return (
    <div
      className="px-4 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors group"
      onClick={() => {
        const url = article.url || `https://www.google.com/search?q=${encodeURIComponent(article.title)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }}
      data-testid={`breakaway-news-article-${ticker}-${idx}`}
    >
      <div className="flex items-start gap-2">
        {article.thumbnail && (
          <img
            src={article.thumbnail}
            alt=""
            loading="lazy"
            className="w-10 h-10 rounded object-contain shrink-0 bg-muted p-0.5"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            data-testid={`breakaway-news-thumb-${ticker}-${idx}`}
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold leading-tight group-hover:text-primary transition-colors line-clamp-2">
            {article.trigger && (
              <span
                className="inline-flex items-center gap-0.5 mr-1.5 px-1 py-px rounded border border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[9px] font-bold tracking-wider align-middle cursor-help"
                title="The headline the Breakaway scanner qualified this stock on"
                data-testid={`breakaway-news-trigger-${ticker}`}
              >
                <Zap className="w-2.5 h-2.5" /> TRIGGER
              </span>
            )}
            {article.title}
          </p>
          <KeywordChipRow article={article} selected={null} onToggle={() => {}} />
          {article.summary && <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{article.summary}</p>}
          <MassiveInsightReasoning article={article} compact />
          <div className="flex items-center gap-2 mt-1 text-[9px] text-muted-foreground flex-wrap">
            {article.publisher_logo && (
              <img src={article.publisher_logo} alt="" className="w-3 h-3 rounded-sm" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            )}
            <span>{article.source}</span>
            {article.market_feed && (
              <span data-testid="breakaway-news-market-feed-tag" className="rounded-sm border border-sky-500/40 bg-sky-500/10 px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-300" title="From Finnhub's swept general / merger market-news feed (no extra API call)">
                Market feed{article.feed === 'merger' ? ' · M&A' : ''}
              </span>
            )}
            <ProviderSentimentChip provider={article.provider} sentiment={article.sentiment} compact />
            <span className="font-mono">{fmtEt(article.published_utc)} ET</span>
            <span>{article.age}</span>
          </div>
        </div>
        <ExternalLink className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary shrink-0 mt-0.5 transition-colors" />
      </div>
    </div>
  );
}
